import React, { useEffect, useMemo, useState } from 'react'
import {
    Box,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import SearchIcon from '@material-ui/icons/Search'

import { Lease } from '../../../../models'
import {
    LeaseBoardUnit,
    activeOccupantsOnArea,
    leasesOnArea,
} from './leaseBoard'

const useStyles = makeStyles((theme: Theme) => ({
    header: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        paddingRight: theme.spacing(1),
    },
    filters: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        margin: theme.spacing(0, 0, 1.5),
    },
    unit: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        padding: theme.spacing(1, 1.25),
        marginBottom: theme.spacing(1),
    },
    unitHead: {
        display: 'flex',
        alignItems: 'baseline',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(0.75),
    },
    beds: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
    },
    bedRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        width: '100%',
        textAlign: 'left',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 6,
        padding: theme.spacing(0.75, 1),
        backgroundColor: '#fff',
        cursor: 'pointer',
        fontFamily: 'inherit',
        color: 'inherit',
        '&:disabled': {
            cursor: 'default',
            opacity: 0.6,
        },
    },
    groupLabel: {
        fontSize: '0.75rem',
        fontWeight: 700,
        margin: theme.spacing(1.5, 0, 0.75),
    },
}))

export function LeaseMoveDialog({
    lease,
    units,
    leases,
    open,
    busy,
    onClose,
    onPick,
}: {
    lease: Lease | null
    units: LeaseBoardUnit[]
    leases: Lease[]
    open: boolean
    busy: boolean
    onClose: () => void
    onPick: (areaId: number) => void
}) {
    const classes = useStyles()
    const [query, setQuery] = useState('')
    const [configFilter, setConfigFilter] = useState<number | 'all'>('all')

    const currentUnit = useMemo(() => {
        if (!lease) return undefined
        return units.find((unit) =>
            unit.areas.some((area) => area.id === lease.area?.id),
        )
    }, [lease, units])

    useEffect(() => {
        if (!open) return
        setQuery('')
        setConfigFilter('all')
    }, [open, lease?.id])

    const configOptions = useMemo(() => {
        const seen = new Map<number, string>()
        for (const unit of units) {
            if (!seen.has(unit.unitConfigId)) {
                seen.set(unit.unitConfigId, unit.unitConfigName || 'Floor plan')
            }
        }
        return Array.from(seen.entries())
            .map(([id, name]) => ({ id, name }))
            .sort((a, b) =>
                a.name.localeCompare(b.name, undefined, { numeric: true }),
            )
    }, [units])

    const grouped = useMemo(() => {
        const needle = query.trim().toLowerCase()
        const visible = units
            .filter((unit) =>
                configFilter === 'all'
                    ? true
                    : unit.unitConfigId === configFilter,
            )
            .filter((unit) => {
                if (!needle) return true
                const occupants = unit.areas
                    .flatMap((area) => leasesOnArea(leases, area.id))
                    .map((row) => row.user?.name || '')
                    .join(' ')
                return `${unit.name} ${unit.unitConfigName} ${unit.folderLabel} ${occupants}`
                    .toLowerCase()
                    .includes(needle)
            })

        const byKey = new Map<string, LeaseBoardUnit[]>()
        for (const unit of visible) {
            const label = unit.unitConfigName || 'Floor plan'
            const existing = byKey.get(label)
            if (existing) existing.push(unit)
            else byKey.set(label, [unit])
        }
        return Array.from(byKey.entries()).sort(([a], [b]) =>
            a.localeCompare(b, undefined, { numeric: true }),
        )
    }, [units, query, configFilter, leases])

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <Box className={classes.header}>
                <DialogTitle disableTypography>
                    <Typography variant="h6">
                        Move to…
                        {lease ? ` — ${lease.user.name}` : ''}
                    </Typography>
                    {currentUnit && (
                        <Typography variant="body2" color="textSecondary">
                            Floor plan: {currentUnit.unitConfigName}
                        </Typography>
                    )}
                </DialogTitle>
                <IconButton onClick={onClose} aria-label="Close">
                    <CloseIcon />
                </IconButton>
            </Box>
            <DialogContent dividers>
                <Box className={classes.filters}>
                    <Chip
                        size="small"
                        clickable
                        label="All floor plans"
                        color={configFilter === 'all' ? 'primary' : 'default'}
                        onClick={() => setConfigFilter('all')}
                    />
                    {configOptions.map((option) => (
                        <Chip
                            key={option.id}
                            size="small"
                            clickable
                            label={option.name}
                            color={
                                configFilter === option.id
                                    ? 'primary'
                                    : 'default'
                            }
                            onClick={() => setConfigFilter(option.id)}
                        />
                    ))}
                    <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Search units or residents"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon
                                        fontSize="small"
                                        color="disabled"
                                    />
                                </InputAdornment>
                            ),
                        }}
                        style={{ flex: '1 1 180px' }}
                    />
                </Box>
                {grouped.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                        No units match this filter.
                    </Typography>
                ) : (
                    grouped.map(([label, groupUnits]) => (
                        <Box key={label}>
                            <Typography className={classes.groupLabel}>
                                {label}
                            </Typography>
                            {groupUnits.map((unit) => {
                                const filled = unit.areas.filter(
                                    (area) =>
                                        activeOccupantsOnArea(
                                            leases,
                                            area.id,
                                            -1,
                                        ).length > 0,
                                ).length
                                return (
                                    <Box key={unit.id} className={classes.unit}>
                                        <Box className={classes.unitHead}>
                                            <Typography
                                                variant="body2"
                                                style={{ fontWeight: 600 }}
                                            >
                                                {unit.name}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="textSecondary"
                                            >
                                                {filled}/{unit.areas.length}
                                            </Typography>
                                        </Box>
                                        <Box className={classes.beds}>
                                            {unit.areas.map((area) => {
                                                const occupants = activeOccupantsOnArea(
                                                    leases,
                                                    area.id,
                                                    lease?.id ?? -1,
                                                )
                                                const primary = occupants[0]
                                                const isCurrent =
                                                    lease != null &&
                                                    lease.area?.id === area.id
                                                const detail = isCurrent
                                                    ? 'Current bed'
                                                    : primary
                                                    ? primary.user.name
                                                    : 'Open'
                                                return (
                                                    <button
                                                        key={area.id}
                                                        type="button"
                                                        className={
                                                            classes.bedRow
                                                        }
                                                        disabled={
                                                            isCurrent || busy
                                                        }
                                                        onClick={() =>
                                                            onPick(area.id)
                                                        }
                                                    >
                                                        <Box
                                                            flex={1}
                                                            minWidth={0}
                                                        >
                                                            <Typography
                                                                variant="body2"
                                                                style={{
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                {
                                                                    area.areaConfigName
                                                                }{' '}
                                                                {area.areaLabel}
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                color="textSecondary"
                                                            >
                                                                {detail}
                                                            </Typography>
                                                        </Box>
                                                        {!isCurrent && (
                                                            <Typography
                                                                variant="body2"
                                                                style={{
                                                                    color:
                                                                        '#008C85',
                                                                    fontWeight: 700,
                                                                    flexShrink: 0,
                                                                }}
                                                            >
                                                                Place
                                                            </Typography>
                                                        )}
                                                    </button>
                                                )
                                            })}
                                        </Box>
                                    </Box>
                                )
                            })}
                        </Box>
                    ))
                )}
            </DialogContent>
        </Dialog>
    )
}
